/**
 * Content-level Search Anchor Registry.
 *
 * Defines individual settings and controls that live INSIDE navigable sections.
 * Each anchor maps to a specific hash-link so the search engine can surface
 * granular items (e.g., "Redis Cache" or "Maintenance Mode") that navigate
 * directly to the containing SettingsSection.
 *
 * These items are merged into the searchable items list alongside nav-config
 * items, inheriting permission gating from their parent section.
 */

import type { SearchMeta } from "./searchIndex.js";

export type SearchAnchor = {
  /** Unique ID for this content item (prefixed with `content-` to avoid collisions) */
  id: string;
  /** Display label shown in search results */
  label: string;
  /** Navigation path including hash anchor (e.g., "/settings/general#feature-flags") */
  path: string;
  /** Icon for search result display */
  icon: string;
  /** Parent section ID (matches navConfig section ID for permission inheritance) */
  sectionId: string;
  /** Parent section label for breadcrumb display */
  sectionLabel: string;
  /** Parent group label(s) for breadcrumb (e.g., "General Settings > Feature Flags") */
  groupLabel: string;
  /** Admin-only gating (matches parent section) */
  adminOnly?: boolean;
  /** Feature flag gating (matches the feature vocabulary in useVisibleSections) */
  requiresFeature?: "wsdashboard" | "webconsole" | "inter-instance-transfer" | "delete-protection";
};

/**
 * Content-level search metadata, keyed by SearchAnchor.id.
 * Follows the same shape as the nav-level searchMetadata.
 */
export const contentSearchMetadata: Record<string, SearchMeta> = {
  // ── General Settings > Instance Settings ────────────────────────────
  "content-instance-name": {
    description: "The display name for your QuickBox Pro instance",
    keywords: ["instance", "name", "title", "quickbox"]
  },
  "content-admin-email": {
    description: "Contact email for system notifications",
    keywords: ["admin", "email", "contact", "notifications"]
  },
  "content-web-root": {
    description: "Base URL for dashboard access (auto-detected if empty)",
    keywords: ["web root", "base url", "hostname", "domain"]
  },

  // ── General Settings > Timezone & Locale ────────────────────────────
  "content-timezone": {
    description: "System timezone for date and time display",
    keywords: ["timezone", "time zone", "utc", "region"]
  },
  "content-date-format": {
    description: "Format used for displaying dates throughout the dashboard",
    keywords: ["date", "format", "display", "iso"]
  },

  // ── General Settings > Display Preferences ──────────────────────────
  "content-default-page": {
    description: "Landing page shown after login for regular users",
    keywords: ["default", "landing", "page", "redirect", "login"]
  },
  "content-admin-default-page": {
    description: "Landing page shown after login for admin users",
    keywords: ["admin", "default", "landing", "page", "redirect"]
  },
  "content-default-theme": {
    description: "Theme applied to new user accounts by default",
    keywords: ["theme", "default", "dark", "light", "appearance"]
  },

  // ── General Settings > Additional Dashboard Settings ────────────────
  "content-disk-mount": {
    description: "Filesystem mount point for disk usage monitoring",
    keywords: ["disk", "mount", "space", "filesystem", "usage", "storage"]
  },
  "content-network-interface": {
    description: "Network interface used for bandwidth statistics",
    keywords: ["network", "interface", "eth0", "nic", "bandwidth", "adapter"]
  },
  "content-vnstat": {
    description: "vnStat bandwidth monitoring configuration",
    keywords: ["vnstat", "bandwidth", "traffic", "monitoring", "start date", "statistics"]
  },
  "content-redis-cache": {
    description: "Redis connection for caching and session storage",
    keywords: ["redis", "cache", "url", "host", "port", "password", "connection", "telemetry"]
  },

  // ── General Settings > Uploads ──────────────────────────────────────
  "content-avatar-max-size": {
    description: "Maximum file size for user avatar uploads",
    keywords: ["avatar", "max", "size", "upload", "kilobytes", "limit"]
  },
  "content-banner-max-size": {
    description: "Maximum file size for user banner uploads",
    keywords: ["banner", "max", "size", "upload", "kilobytes", "limit"]
  },
  "content-allow-avatar-upload": {
    description: "Toggle to let users upload custom profile avatars",
    keywords: ["avatar", "upload", "allow", "profile", "picture"]
  },

  // ── General Settings > Branding ─────────────────────────────────────
  "content-collapsed-logo": {
    description: "Small logo shown when sidebar is collapsed",
    keywords: ["collapsed", "logo", "sidebar", "icon", "small"]
  },
  "content-full-logo": {
    description: "Full logo shown when sidebar is expanded",
    keywords: ["full", "logo", "sidebar", "expanded", "brand"]
  },
  "content-favicon": {
    description: "Browser tab favicon for the dashboard",
    keywords: ["favicon", "icon", "tab", "browser", "bookmark"]
  },
  "content-default-avatar": {
    description: "Default avatar used for users without a custom avatar",
    keywords: ["default", "avatar", "profile", "placeholder", "picture"]
  },

  // ── General Settings > Auth Pages ───────────────────────────────────
  "content-auth-show-logo": {
    description: "Show or hide the logo on login and registration pages",
    keywords: ["logo", "login", "register", "auth", "show", "hide"]
  },
  "content-auth-login-message": {
    description: "Custom welcome message displayed on auth pages (Markdown supported)",
    keywords: ["login", "message", "welcome", "markdown", "custom", "auth"]
  },

  // ── General Settings > Feature Flags ────────────────────────────────
  "content-maintenance-mode": {
    description: "Show maintenance page to non-admin users",
    keywords: ["maintenance", "mode", "offline", "downtime", "unavailable"]
  },
  "content-user-registration": {
    description: "Allow or block new user registrations",
    keywords: ["registration", "signup", "register", "new users", "open", "closed"]
  },
  "content-notification-channels": {
    description: "Control external notification channel access for non-admin users",
    keywords: ["notification", "channels", "discord", "signal", "whatsapp", "external"]
  },
  "content-web-console": {
    description: "Browser-based terminal for admin server access",
    keywords: ["web console", "terminal", "shell", "bash", "ssh", "command line"]
  },
  "content-delete-protection": {
    description: "Guard the server against freezes caused by large file deletes on media and download apps",
    keywords: ["delete protection", "large delete", "safeguard", "unlink", "media", "download", "freeze", "feature flag", "enable"]
  },
  "content-mask-banned-ips": {
    description: "Hide the last octet of banned attacker IPs on the Fail2Ban dashboard",
    keywords: ["mask", "banned", "ip", "octet", "privacy", "fail2ban", "attacker", "hide", "redact", "address"]
  },
  "content-auto-protect-apps": {
    description: "Automatically enable Fail2Ban brute-force protection for supported apps on install",
    keywords: ["auto-protect", "auto protect", "fail2ban", "brute-force", "install", "protection", "apps", "jail", "automatic"]
  },
  "content-transfer-toggle": {
    description: "Enable moving data between your own QuickBox instances",
    keywords: ["transfer", "inter-instance", "instance", "migrate", "move", "data transfer", "send", "receive", "feature flag", "enable"]
  },
  "content-wsdashboard": {
    description: "Real-time streaming session monitor for Emby and Jellyfin",
    keywords: ["wsdashboard", "streaming", "monitor", "emby", "jellyfin", "media", "sessions"]
  },
  "content-playback-mirror": {
    description: "Mirror active playback sessions in read-only mode",
    keywords: ["playback", "mirror", "stream", "watch", "read-only", "ttl"]
  },

  // ── Session Settings > Timeouts ─────────────────────────────────────
  "content-user-timeout": {
    description: "Minutes of inactivity before a user session expires",
    keywords: ["user", "timeout", "inactivity", "session", "expire", "minutes"]
  },
  "content-guest-timeout": {
    description: "Minutes of inactivity before a guest session expires",
    keywords: ["guest", "timeout", "inactivity", "session", "expire", "minutes"]
  },

  // ── Session Settings > Persistence & Cookies ────────────────────────
  "content-remember-me": {
    description: "Duration for 'Remember me' cookies before expiry",
    keywords: ["remember", "me", "cookie", "duration", "days", "persistent"]
  },
  "content-auto-extend": {
    description: "Reset session timer on each request to keep active users logged in",
    keywords: ["auto-extend", "activity", "session", "persistent", "rolling", "refresh"]
  },

  // ── Session Settings > Concurrency ──────────────────────────────────
  "content-multiple-sessions": {
    description: "Allow or block simultaneous login sessions per user",
    keywords: ["multiple", "sessions", "simultaneous", "concurrent", "device", "revoke"]
  },

  // ── Session Settings > Session Operations ───────────────────────────
  "content-invalidate-sessions": {
    description: "Force all non-admin users to log in again",
    keywords: ["invalidate", "revoke", "force", "logout", "non-admin", "bulk"]
  },

  // ── Security Settings > Disallowed Usernames ────────────────────────
  "content-disallowed-usernames": {
    description: "Manage usernames blocked from registration and login",
    keywords: ["disallowed", "usernames", "blocked", "reserved", "registration", "blocklist"]
  },

  // ── Security Settings > Banned IPs ──────────────────────────────────
  "content-banned-ips": {
    description: "Block IP addresses with application-level and iptables enforcement",
    keywords: ["banned", "ip", "block", "firewall", "iptables", "address"]
  },

  // ── Security Settings > Network Enforcement ─────────────────────────
  "content-network-enforcement": {
    description: "Manage iptables-level IP/CIDR blocks via the QBX_ENFORCEMENT chain",
    keywords: ["network", "enforcement", "iptables", "cidr", "block", "qbx", "chain", "firewall"]
  },

  // ── Registration Settings > Activation ──────────────────────────────
  "content-activation-mode": {
    description: "How new accounts are activated after registration",
    keywords: ["activation", "mode", "email", "admin", "disabled", "approval", "verify"]
  },

  // ── Registration Settings > Quotas ──────────────────────────────────
  "content-default-quota": {
    description: "Default disk quota assigned to new user accounts",
    keywords: ["quota", "disk", "unlimited", "storage", "limit", "gb", "tb", "mb"]
  },

  // ── Registration Settings > Default Group ───────────────────────────
  "content-default-group": {
    description: "Default user group assigned to new registrations",
    keywords: ["default", "group", "registration", "assign", "access", "membership", "new user", "role"]
  },

  // ── Registration Settings > Username Policy ─────────────────────────
  "content-username-min-length": {
    description: "Minimum character length for usernames",
    keywords: ["username", "minimum", "length", "characters", "min"]
  },
  "content-username-max-length": {
    description: "Maximum character length for usernames",
    keywords: ["username", "maximum", "length", "characters", "max"]
  },
  "content-force-lowercase": {
    description: "Automatically convert usernames to lowercase during registration",
    keywords: ["lowercase", "username", "force", "case", "convert"]
  },
  "content-username-regex": {
    description: "Custom regex pattern for username validation",
    keywords: ["username", "regex", "pattern", "validation", "custom", "format"]
  },

  // ── Registration Settings > Password Policy ─────────────────────────
  "content-password-min-length": {
    description: "Minimum character length for passwords",
    keywords: ["password", "minimum", "length", "characters", "min"]
  },
  "content-password-max-length": {
    description: "Maximum character length for passwords",
    keywords: ["password", "maximum", "length", "characters", "max"]
  },

  // ── Registration Settings > Welcome Email ───────────────────────────
  "content-welcome-email": {
    description: "Send a welcome email to new users after registration",
    keywords: ["welcome", "email", "send", "new user", "registration", "notification"]
  },

  // ── Security Settings > Firewall Rules Tab ────────────────────────────
  "content-firewall-origin-filter": {
    description: "Filter firewall rules by origin: QuickBox, Fail2Ban, WireGuard, Geo Policy, or System",
    keywords: ["filter", "origin", "quickbox", "fail2ban", "wireguard", "geo", "system", "category"]
  },
  "content-firewall-bulk-delete": {
    description: "Select and bulk-remove QBX-managed firewall rules",
    keywords: ["bulk", "delete", "remove", "select", "clear", "rules", "mass"]
  },

  // ── Security Settings > Geo-Lock Tab ──────────────────────────────────
  "content-geo-lock-add-rule": {
    description: "Create geo-lock rules to restrict streaming access by country",
    keywords: ["add", "create", "rule", "geo", "lock", "country", "streaming", "restrict"]
  },
  "content-geo-lock-violations": {
    description: "View recent geo-lock violation events and blocked access attempts",
    keywords: ["violations", "blocked", "attempts", "denied", "events", "recent"]
  },

  // ── Security Settings > Geo-Block Tab ─────────────────────────────────
  "content-geo-block-countries": {
    description: "Select countries to block or allow in the geo-block policy",
    keywords: ["countries", "blocklist", "allowlist", "select", "add", "remove", "policy"]
  },
  "content-geo-block-enforcement": {
    description: "Apply or disable geo-block firewall enforcement",
    keywords: ["apply", "enforce", "activate", "disable", "firewall", "active", "rules"]
  },
  "content-geo-block-ip-test": {
    description: "Test whether a specific IP address would be allowed or blocked",
    keywords: ["test", "ip", "check", "verify", "allowed", "blocked", "address"]
  },

  // ── Transfers ─────────────────────────────────────────────────────────
  "content-transfers-send": {
    description: "Send data to one of your other QuickBox instances and track outgoing offers",
    keywords: ["send", "outgoing", "create", "offer", "push", "transfer", "share", "move", "data"]
  },
  "content-transfers-incoming": {
    description: "Review and approve incoming transfer offers from your other instances",
    keywords: ["incoming", "receive", "inbox", "approve", "review", "pending", "offer", "accept", "pull"]
  },
  "content-transfers-history": {
    description: "View completed and terminal transfers, incoming and outgoing",
    keywords: ["history", "completed", "past", "log", "record", "finished", "transfers"]
  },
  "content-transfers-settings": {
    description: "Configure transfer defaults: throttle, off-peak window, chunk size and data port",
    keywords: ["settings", "throttle", "bandwidth", "off-peak", "schedule", "chunk", "port", "defaults", "configure"]
  },

  // ── Delete Protection (under System) ────────────────────────────────────
  "content-delete-protection-restore": {
    description: "Review recent large-file deletes and restore or prune protected deletions",
    keywords: ["restore", "recover", "recent deletes", "undelete", "prune", "unlink", "banked", "protected", "delete protection"]
  },
  "content-delete-protection-services": {
    description: "Per-app delete-protection status and enable/disable toggles",
    keywords: ["services", "protected", "apps", "toggle", "enable", "systemd", "manual", "status", "delete protection"]
  },
  "content-delete-protection-custom": {
    description: "Register a custom app for delete protection with its own recipe",
    keywords: ["custom", "app", "add", "registry", "recipe", "protect", "delete protection"]
  },

  // ── System Dashboard ────────────────────────────────────────────────────
  // "Overview" is a nav child (sd-overview), searchable AS the nav item, so it
  // carries no duplicate content anchor here (a section that is a nav child is
  // not also a content anchor — see Addendum 3).
  "content-sysdash-quick-actions": {
    description: "Quick action shortcuts for common system operations",
    keywords: ["quick", "actions", "shortcuts", "restart", "reload", "controls", "buttons"]
  },
  "content-sysdash-telemetry-sources": {
    description: "Configured telemetry collectors and metric sources",
    keywords: ["telemetry", "sources", "collectors", "agents", "feeds", "providers", "metrics source"]
  },
  "content-sysdash-health-details": {
    description: "Detailed per-service health checks and status breakdown",
    keywords: ["health", "details", "breakdown", "checks", "status", "diagnostics", "per-service"]
  },
  "content-sysdash-health-score": {
    description: "Overall system health score and rating",
    keywords: ["health score", "score", "rating", "overall", "grade", "system health", "index"]
  },

  // ── VPN Control ─────────────────────────────────────────────────────────
  "content-vpn-peers": {
    description: "WireGuard peer configurations, client profiles and tunnels",
    keywords: ["wireguard", "peer", "tunnel", "client", "profile", "config", "vpn", "wg"]
  },
  "content-vpn-upload": {
    description: "Upload and import a client VPN configuration file",
    keywords: ["upload", "import", "config", "wireguard", "ovpn", "file", "vpn"]
  },
  "content-vpn-app-routing": {
    description: "Route specific apps through the VPN tunnel (split tunneling)",
    keywords: ["app routing", "split tunnel", "per-app", "subnet", "route", "bind", "vpn"]
  },
  "content-vpn-history": {
    description: "VPN connection history and past sessions",
    keywords: ["connection", "history", "sessions", "log", "vpn", "past", "activity"]
  },
  "content-vpn-openvpn": {
    description: "OpenVPN server status and configuration",
    keywords: ["openvpn", "ovpn", "server", "vpn", "gateway", "status"]
  },
  "content-vpn-lifecycle": {
    description: "Config lifecycle policy: rotation and expiry of VPN client configs",
    keywords: ["lifecycle", "rotation", "expiry", "policy", "config", "vpn", "renew"]
  },
  "content-vpn-killswitch": {
    description: "VPN kill switch and leak-protection failsafe",
    keywords: ["kill switch", "failsafe", "leak", "protection", "block", "vpn"]
  },

  // ── SSL Control ─────────────────────────────────────────────────────────
  "content-ssl-installed": {
    description: "View installed SSL/TLS certificates and their status",
    keywords: ["certificates", "ssl", "tls", "list", "installed", "expiry", "https"]
  },
  "content-ssl-issue": {
    description: "Issue a new SSL certificate via Let's Encrypt",
    keywords: ["new certificate", "issue", "letsencrypt", "acme", "request", "dns", "https", "generate"]
  },

  // ── App Templates ───────────────────────────────────────────────────────
  "content-apptpl-defaults": {
    description: "Default application definition templates",
    keywords: ["defaults", "template", "app definition", "default", "baseline"]
  },
  "content-apptpl-customized": {
    description: "Customized application templates that override the defaults",
    keywords: ["customized", "override", "template", "edit", "custom", "app"]
  },

  // ── API Control ─────────────────────────────────────────────────────────
  "content-api-keys": {
    description: "Generate, view and revoke API keys and tokens",
    keywords: ["api key", "token", "generate", "revoke", "integration", "credential", "secret"]
  },

  // ── Audit Log ───────────────────────────────────────────────────────────
  "content-audit-log": {
    description: "Immutable audit trail of admin and system actions",
    keywords: ["audit", "trail", "events", "actions", "history", "who", "changes", "log", "accountability"]
  },

  // ── Package Management ──────────────────────────────────────────────────
  "content-package-management": {
    description: "Install, remove, reinstall and update applications",
    keywords: ["install", "remove", "reinstall", "update", "package", "software", "apps", "manage", "packages"]
  },

  // ── User Admin ──────────────────────────────────────────────────────────
  "content-users-create": {
    description: "Create a new user account",
    keywords: ["create user", "add user", "new account", "new user", "register", "provision", "account"]
  },
};

/**
 * Static registry of content-level search anchors.
 * Each entry maps a specific setting or control to a navigable hash-link.
 */
export const contentSearchAnchors: SearchAnchor[] = [
  // ── General Settings > Instance Settings ────────────────────────────
  {
    id: "content-instance-name",
    label: "Instance Name",
    path: "/settings/general#instance-settings",
    icon: "lucide:type",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Instance Settings",
    adminOnly: true
  },
  {
    id: "content-admin-email",
    label: "Admin Email",
    path: "/settings/general#instance-settings",
    icon: "lucide:mail",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Instance Settings",
    adminOnly: true
  },
  {
    id: "content-web-root",
    label: "Web Root",
    path: "/settings/general#instance-settings",
    icon: "lucide:globe",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Instance Settings",
    adminOnly: true
  },

  // ── General Settings > Timezone & Locale ────────────────────────────
  {
    id: "content-timezone",
    label: "System Timezone",
    path: "/settings/general#timezone-locale",
    icon: "lucide:clock",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Timezone & Locale",
    adminOnly: true
  },
  {
    id: "content-date-format",
    label: "Date Format",
    path: "/settings/general#timezone-locale",
    icon: "lucide:calendar",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Timezone & Locale",
    adminOnly: true
  },

  // ── General Settings > Display Preferences ──────────────────────────
  {
    id: "content-default-page",
    label: "User Default Page",
    path: "/settings/general#display-preferences",
    icon: "lucide:home",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Display Preferences",
    adminOnly: true
  },
  {
    id: "content-admin-default-page",
    label: "Admin Default Page",
    path: "/settings/general#display-preferences",
    icon: "lucide:shield",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Display Preferences",
    adminOnly: true
  },
  {
    id: "content-default-theme",
    label: "Default Theme",
    path: "/settings/general#display-preferences",
    icon: "lucide:palette",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Display Preferences",
    adminOnly: true
  },

  // ── General Settings > Additional Dashboard Settings ────────────────
  {
    id: "content-disk-mount",
    label: "Disk Space Monitor",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:hard-drive",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-network-interface",
    label: "Network Interface",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:network",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-vnstat",
    label: "vnStat Bandwidth",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:bar-chart",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-redis-cache",
    label: "Redis Cache",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:database",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Additional Dashboard Settings",
    adminOnly: true
  },

  // ── General Settings > Uploads ──────────────────────────────────────
  {
    id: "content-avatar-max-size",
    label: "Avatar Max Size",
    path: "/settings/general#uploads",
    icon: "lucide:image",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Uploads",
    adminOnly: true
  },
  {
    id: "content-banner-max-size",
    label: "Banner Max Size",
    path: "/settings/general#uploads",
    icon: "lucide:image",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Uploads",
    adminOnly: true
  },
  {
    id: "content-allow-avatar-upload",
    label: "Allow Avatar Uploads",
    path: "/settings/general#uploads",
    icon: "lucide:upload",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Uploads",
    adminOnly: true
  },

  // ── General Settings > Branding ─────────────────────────────────────
  {
    id: "content-collapsed-logo",
    label: "Collapsed Logo",
    path: "/settings/general#branding",
    icon: "lucide:image",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Branding",
    adminOnly: true
  },
  {
    id: "content-full-logo",
    label: "Full Logo",
    path: "/settings/general#branding",
    icon: "lucide:image",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Branding",
    adminOnly: true
  },
  {
    id: "content-favicon",
    label: "Favicon",
    path: "/settings/general#branding",
    icon: "lucide:bookmark",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Branding",
    adminOnly: true
  },
  {
    id: "content-default-avatar",
    label: "Default Avatar",
    path: "/settings/general#branding",
    icon: "lucide:user",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Branding",
    adminOnly: true
  },

  // ── General Settings > Auth Pages ───────────────────────────────────
  {
    id: "content-auth-show-logo",
    label: "Auth Page Logo",
    path: "/settings/general#auth-pages",
    icon: "lucide:image",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Auth Pages",
    adminOnly: true
  },
  {
    id: "content-auth-login-message",
    label: "Login Message",
    path: "/settings/general#auth-pages",
    icon: "lucide:message-square",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Auth Pages",
    adminOnly: true
  },

  // ── General Settings > Feature Flags ────────────────────────────────
  {
    id: "content-maintenance-mode",
    label: "Maintenance Mode",
    path: "/settings/general#feature-flags",
    icon: "lucide:construction",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-user-registration",
    label: "User Registration Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:user-plus",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-notification-channels",
    label: "External Notification Channels",
    path: "/settings/general#feature-flags",
    icon: "lucide:bell-off",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-web-console",
    label: "Web Console Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:terminal",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    // The toggle itself is NOT feature-gated: an admin must be able to find it
    // in search to ENABLE the feature when it is currently off. Matches the
    // sibling Feature Flags toggles (Web Console, Transfer, WSDashboard).
    id: "content-delete-protection",
    label: "Delete Protection Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:shield-alert",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-mask-banned-ips",
    label: "Mask Banned IP Addresses",
    path: "/settings/general#feature-flags",
    icon: "lucide:eye-off",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-auto-protect-apps",
    label: "Auto-Protect Apps on Install",
    path: "/settings/general#feature-flags",
    icon: "lucide:shield-check",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-transfer-toggle",
    label: "Inter-Instance Transfer Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:arrow-left-right",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-wsdashboard",
    label: "WSDashboard Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:tv",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },
  {
    id: "content-playback-mirror",
    label: "Playback Mirror",
    path: "/settings/general#feature-flags",
    icon: "lucide:copy",
    sectionId: "settings-general",
    sectionLabel: "General Settings",
    groupLabel: "Feature Flags",
    adminOnly: true
  },

  // ── Session Settings > Timeouts ─────────────────────────────────────
  {
    id: "content-user-timeout",
    label: "User Inactivity Timeout",
    path: "/settings/sessions#timeouts",
    icon: "lucide:timer",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Timeouts",
    adminOnly: true
  },
  {
    id: "content-guest-timeout",
    label: "Guest Timeout",
    path: "/settings/sessions#timeouts",
    icon: "lucide:timer",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Timeouts",
    adminOnly: true
  },

  // ── Session Settings > Persistence & Cookies ────────────────────────
  {
    id: "content-remember-me",
    label: "Remember Me Duration",
    path: "/settings/sessions#persistence-cookies",
    icon: "lucide:cookie",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Persistence & Cookies",
    adminOnly: true
  },
  {
    id: "content-auto-extend",
    label: "Auto-Extend on Activity",
    path: "/settings/sessions#persistence-cookies",
    icon: "lucide:refresh-cw",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Persistence & Cookies",
    adminOnly: true
  },

  // ── Session Settings > Concurrency ──────────────────────────────────
  {
    id: "content-multiple-sessions",
    label: "Allow Multiple Sessions",
    path: "/settings/sessions#concurrency",
    icon: "lucide:users",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Concurrency",
    adminOnly: true
  },

  // ── Session Settings > Session Operations ───────────────────────────
  {
    id: "content-invalidate-sessions",
    label: "Invalidate Non-Admin Sessions",
    path: "/settings/sessions#session-operations",
    icon: "lucide:shield-alert",
    sectionId: "settings-sessions",
    sectionLabel: "Session Settings",
    groupLabel: "Session Operations",
    adminOnly: true
  },

  // ── Security Settings > Disallowed Usernames ────────────────────────
  {
    id: "content-disallowed-usernames",
    label: "Disallowed Usernames",
    path: "/settings/security#disallowed-usernames",
    icon: "lucide:user-x",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Disallowed Usernames",
    adminOnly: true
  },

  // ── Security Settings > Banned IPs ──────────────────────────────────
  {
    id: "content-banned-ips",
    label: "Banned IPs",
    path: "/settings/security#banned-ips",
    icon: "lucide:shield-ban",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Banned IPs",
    adminOnly: true
  },

  // ── Security Settings > Network Enforcement ─────────────────────────
  {
    id: "content-network-enforcement",
    label: "Network Enforcement",
    path: "/settings/security#network-enforcement",
    icon: "lucide:shield-alert",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Network Enforcement",
    adminOnly: true
  },

  // ── Registration Settings > Activation ──────────────────────────────
  {
    id: "content-activation-mode",
    label: "Account Activation Mode",
    path: "/settings/registration#activation",
    icon: "lucide:user-check",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Activation",
    adminOnly: true
  },

  // ── Registration Settings > Quotas ──────────────────────────────────
  {
    id: "content-default-quota",
    label: "Default Disk Quota",
    path: "/settings/registration#quotas",
    icon: "lucide:hard-drive",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Quotas",
    adminOnly: true
  },

  // ── Registration Settings > Default Group ───────────────────────────
  {
    id: "content-default-group",
    label: "Default Registration Group",
    path: "/settings/registration#default-group",
    icon: "lucide:users",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Default Group",
    adminOnly: true
  },

  // ── Registration Settings > Username Policy ─────────────────────────
  {
    id: "content-username-min-length",
    label: "Username Minimum Length",
    path: "/settings/registration#username-policy",
    icon: "lucide:text-cursor-input",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Username Policy",
    adminOnly: true
  },
  {
    id: "content-username-max-length",
    label: "Username Maximum Length",
    path: "/settings/registration#username-policy",
    icon: "lucide:text-cursor-input",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Username Policy",
    adminOnly: true
  },
  {
    id: "content-force-lowercase",
    label: "Force Lowercase Usernames",
    path: "/settings/registration#username-policy",
    icon: "lucide:case-lower",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Username Policy",
    adminOnly: true
  },
  {
    id: "content-username-regex",
    label: "Username Regex Pattern",
    path: "/settings/registration#username-policy",
    icon: "lucide:regex",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Username Policy",
    adminOnly: true
  },

  // ── Registration Settings > Password Policy ─────────────────────────
  {
    id: "content-password-min-length",
    label: "Password Minimum Length",
    path: "/settings/registration#password-policy",
    icon: "lucide:key",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Password Policy",
    adminOnly: true
  },
  {
    id: "content-password-max-length",
    label: "Password Maximum Length",
    path: "/settings/registration#password-policy",
    icon: "lucide:key",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Password Policy",
    adminOnly: true
  },

  // ── Registration Settings > Welcome Email ───────────────────────────
  {
    id: "content-welcome-email",
    label: "Send Welcome Email",
    path: "/settings/registration#welcome-email",
    icon: "lucide:mail-check",
    sectionId: "settings-registration",
    sectionLabel: "Registration Settings",
    groupLabel: "Welcome Email",
    adminOnly: true
  },

  // ── Security Settings > Firewall Rules Tab ────────────────────────────
  {
    id: "content-firewall-origin-filter",
    label: "Firewall Rule Origin Filter",
    path: "/settings/security?tab=firewall",
    icon: "lucide:filter",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Firewall Rules",
    adminOnly: true
  },
  {
    id: "content-firewall-bulk-delete",
    label: "Bulk Delete Firewall Rules",
    path: "/settings/security?tab=firewall",
    icon: "lucide:trash-2",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Firewall Rules",
    adminOnly: true
  },

  // ── Security Settings > Geo-Lock Tab ──────────────────────────────────
  {
    id: "content-geo-lock-add-rule",
    label: "Add Geo-Lock Rule",
    path: "/settings/security?tab=geo-lock",
    icon: "lucide:plus",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Geo-Lock",
    adminOnly: true
  },
  {
    id: "content-geo-lock-violations",
    label: "Geo-Lock Violations",
    path: "/settings/security?tab=geo-lock",
    icon: "lucide:alert-triangle",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Geo-Lock",
    adminOnly: true
  },

  // ── Security Settings > Geo-Block Tab ─────────────────────────────────
  {
    id: "content-geo-block-countries",
    label: "Blocked Countries List",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:map",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Geo-Block",
    adminOnly: true
  },
  {
    id: "content-geo-block-enforcement",
    label: "Geo-Block Enforcement",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:shield",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Geo-Block",
    adminOnly: true
  },
  {
    id: "content-geo-block-ip-test",
    label: "Geo-Block IP Test",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:search",
    sectionId: "settings-security",
    sectionLabel: "Security Settings",
    groupLabel: "Geo-Block",
    adminOnly: true
  },

  // ── Instance Transfer (under System) ──────────────────────────────────
  {
    id: "content-transfers-send",
    label: "Send Transfer",
    path: "/transfers?tab=send",
    icon: "lucide:send",
    sectionId: "transfers",
    sectionLabel: "Inter-Instance Transfer",
    groupLabel: "Send",
    adminOnly: true,
    requiresFeature: "inter-instance-transfer"
  },
  {
    id: "content-transfers-incoming",
    label: "Incoming Transfers",
    path: "/transfers?tab=incoming",
    icon: "lucide:inbox",
    sectionId: "transfers",
    sectionLabel: "Inter-Instance Transfer",
    groupLabel: "Incoming",
    adminOnly: true,
    requiresFeature: "inter-instance-transfer"
  },
  {
    id: "content-transfers-history",
    label: "Transfer History",
    path: "/transfers?tab=history",
    icon: "lucide:history",
    sectionId: "transfers",
    sectionLabel: "Inter-Instance Transfer",
    groupLabel: "History",
    adminOnly: true,
    requiresFeature: "inter-instance-transfer"
  },
  {
    id: "content-transfers-settings",
    label: "Transfer Settings",
    path: "/transfers?tab=settings",
    icon: "lucide:settings",
    sectionId: "transfers",
    sectionLabel: "Inter-Instance Transfer",
    groupLabel: "Settings",
    adminOnly: true,
    requiresFeature: "inter-instance-transfer"
  },

  // ── Delete Protection (under System) ──────────────────────────────────
  // Feature-gated: these controls only exist once Delete Protection is on,
  // so they are hidden until then (the Feature Flags toggle is how it gets
  // enabled). Mirrors the reachability of the /system/delete-protection page.
  {
    id: "content-delete-protection-restore",
    label: "Restore Deleted Files",
    path: "/system/delete-protection#delete-protection-restore",
    icon: "lucide:rotate-ccw",
    sectionId: "system-delete-protection",
    sectionLabel: "Delete Protection",
    groupLabel: "Restore & History",
    adminOnly: true,
    requiresFeature: "delete-protection"
  },
  {
    id: "content-delete-protection-services",
    label: "Protected Services",
    path: "/system/delete-protection#delete-protection-services",
    icon: "lucide:shield-check",
    sectionId: "system-delete-protection",
    sectionLabel: "Delete Protection",
    groupLabel: "Protected Services",
    adminOnly: true,
    requiresFeature: "delete-protection"
  },
  {
    id: "content-delete-protection-custom",
    label: "Custom Protected App",
    path: "/system/delete-protection#delete-protection-custom",
    icon: "lucide:plus",
    sectionId: "system-delete-protection",
    sectionLabel: "Delete Protection",
    groupLabel: "Custom App",
    adminOnly: true,
    requiresFeature: "delete-protection"
  },

  // ── System Dashboard (under System) ───────────────────────────────────
  // "Overview" is a Quick-Nav child (sd-overview) — searchable as the nav item,
  // so no duplicate content anchor for /system/dashboard#sysdash-overview.
  {
    id: "content-sysdash-quick-actions",
    label: "Quick Actions",
    path: "/system/dashboard#sysdash-quick-actions",
    icon: "lucide:zap",
    sectionId: "system-dashboard",
    sectionLabel: "System Dashboard",
    groupLabel: "Quick Actions",
    adminOnly: true
  },
  {
    id: "content-sysdash-telemetry-sources",
    label: "Telemetry Sources",
    path: "/system/dashboard#sysdash-telemetry-sources",
    icon: "lucide:radio-tower",
    sectionId: "system-dashboard",
    sectionLabel: "System Dashboard",
    groupLabel: "Telemetry Sources",
    adminOnly: true
  },
  {
    id: "content-sysdash-health-details",
    label: "Health Details",
    path: "/system/dashboard#sysdash-health-details",
    icon: "lucide:list-checks",
    sectionId: "system-dashboard",
    sectionLabel: "System Dashboard",
    groupLabel: "Health Details",
    adminOnly: true
  },
  {
    id: "content-sysdash-health-score",
    label: "System Health Score",
    path: "/system/dashboard#sysdash-health-score",
    icon: "lucide:gauge-circle",
    sectionId: "system-dashboard",
    sectionLabel: "System Dashboard",
    groupLabel: "System Health Score",
    adminOnly: true
  },

  // ── VPN Control (under System) ────────────────────────────────────────
  // Admin-only. The page also requires the WireGuard app installed; content
  // anchors cannot express `requiresApp`, so an admin without WireGuard may
  // see these and land on the page's not-installed state (no data leak).
  {
    id: "content-vpn-peers",
    label: "Peer Configurations",
    path: "/system/vpn#vpn-peers",
    icon: "lucide:users",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "Peer Configurations",
    adminOnly: true
  },
  {
    id: "content-vpn-upload",
    label: "Upload Client Config",
    path: "/system/vpn#vpn-upload",
    icon: "lucide:upload",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "Upload Client Config",
    adminOnly: true
  },
  {
    id: "content-vpn-app-routing",
    label: "App-Scoped Routing",
    path: "/system/vpn#vpn-app-routing",
    icon: "lucide:route",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "App-Scoped Routing",
    adminOnly: true
  },
  {
    id: "content-vpn-history",
    label: "Connection History",
    path: "/system/vpn",
    icon: "lucide:history",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "Connection History",
    adminOnly: true
  },
  {
    id: "content-vpn-openvpn",
    label: "OpenVPN Server",
    path: "/system/vpn",
    icon: "lucide:server",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "OpenVPN Server",
    adminOnly: true
  },
  {
    id: "content-vpn-lifecycle",
    label: "Config Lifecycle Policy",
    path: "/system/vpn#vpn-lifecycle",
    icon: "lucide:refresh-cw",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "Config Lifecycle Policy",
    adminOnly: true
  },
  {
    id: "content-vpn-killswitch",
    label: "Kill Switch",
    path: "/system/vpn#vpn-app-routing",
    icon: "lucide:power-off",
    sectionId: "system-vpn",
    sectionLabel: "VPN Control",
    groupLabel: "Kill Switch",
    adminOnly: true
  },

  // ── SSL Control (under System) ────────────────────────────────────────
  {
    id: "content-ssl-installed",
    label: "Installed Certificates",
    path: "/system/ssl#ssl-installed",
    icon: "lucide:award",
    sectionId: "system-ssl",
    sectionLabel: "SSL Control",
    groupLabel: "Installed Certificates",
    adminOnly: true
  },
  {
    id: "content-ssl-issue",
    label: "Issue New Certificate",
    path: "/system/ssl#ssl-issue",
    icon: "lucide:file-plus",
    sectionId: "system-ssl",
    sectionLabel: "SSL Control",
    groupLabel: "Issue New Certificate",
    adminOnly: true
  },

  // ── App Templates (under System) ──────────────────────────────────────
  {
    id: "content-apptpl-defaults",
    label: "Default Templates",
    path: "/system/app-templates#apptpl-defaults",
    icon: "lucide:package",
    sectionId: "system-app-templates",
    sectionLabel: "App Templates",
    groupLabel: "Default Templates",
    adminOnly: true
  },
  {
    id: "content-apptpl-customized",
    label: "Customized Templates",
    path: "/system/app-templates#apptpl-defaults",
    icon: "lucide:package-open",
    sectionId: "system-app-templates",
    sectionLabel: "App Templates",
    groupLabel: "Customized Templates",
    adminOnly: true
  },

  // ── API Control (under System) ────────────────────────────────────────
  {
    id: "content-api-keys",
    label: "API Keys",
    path: "/system/api",
    icon: "lucide:key-round",
    sectionId: "system-api",
    sectionLabel: "API Control",
    groupLabel: "API Keys",
    adminOnly: true
  },

  // ── Audit Log (under System, non-nav route) ───────────────────────────
  {
    id: "content-audit-log",
    label: "Audit Log",
    path: "/system/audit-log",
    icon: "lucide:scroll",
    sectionId: "system-audit",
    sectionLabel: "System",
    groupLabel: "Audit Log",
    adminOnly: true
  },

  // ── Package Management (App Dashboard, packages mode) ──────────────────
  {
    id: "content-package-management",
    label: "Package Management",
    path: "/dashboard?mode=packages",
    icon: "lucide:package-plus",
    sectionId: "dashboard-packages",
    sectionLabel: "App Dashboard",
    groupLabel: "Package Management",
    adminOnly: true
  },

  // ── User Admin (under User Management) ────────────────────────────────
  {
    id: "content-users-create",
    label: "Create User",
    path: "/users/admin",
    icon: "lucide:user-plus",
    sectionId: "user-admin",
    sectionLabel: "User Admin",
    groupLabel: "Create User",
    adminOnly: true
  },
];
